import type { CustomAxiosRequestConfig } from "@/types/common.types";
import axiosInstance from "@/lib/axiosInstance";
import {
  buildFilterByParam,
  buildFilterByObject,
  type CustomFilterHandler,
} from "@/utils/buildFilterByParam";
import { toStoredCalendarDay, toValidDate } from "@/utils/dateFormatter";
import { getQueryParamsWithCustomQueryObj } from "@/utils/getQueryParamsFromCustomQueryObject";
import type { TSearchParams } from "@/components/ui/Chips/FilterChip/types";
import { workOrdersEndpoints } from "@/api/clients/WorkOrders/work-orders.endpoints";
import { workOrderEndpoints } from "@/api/work-orders/work-order.endpoints";
import type {
  WorkOrderListResponse,
  WorkOrderListQueryOptions,
  ActiveTabType,
  WorkOrder,
  CompletedWorkOrder,
  CompletedTabType,
  UpcomingTabType,
  UpcomingVisitApiResponse,
  WorkOrderDetailsResponse,
  TWorkOrderIntakeDraft,
  TWorkOrderIntakeDraftsResponse,
} from "./workOrders.types";
import type {
  TCompletedWorkOrder,
  TUpcomingWOResponse,
  TWODataRequest,
  TWorkOrderByIdResponse,
  GeographyCitiesResponse,
  GeographyRegion,
} from "./workOrder.queries.types";
import type { RequestQueryOptions } from "@/api/service-provider/serviceProvider.types.ts";

// ── Shared date/range filter helpers for work-order list APIs ──────────────

/** Parse "startIso|endIso" into { gte, lte } in UTC ISO format (normalised so lte >= gte). Returns null if invalid. */
function parseDateRangeToGteLte(raw: string): { gte: string; lte: string } | null {
  const [startIso, endIso] = raw.split("|");
  const startDate = toValidDate(startIso);
  const endDate = toValidDate(endIso);
  if (!startDate || !endDate) return null;

  if (endDate < startDate) {
    return { gte: endDate.toISOString(), lte: startDate.toISOString() };
  }
  return { gte: startDate.toISOString(), lte: endDate.toISOString() };
}

/**
 * Shared by every list whose date columns filter as a chip: a single date
 * becomes that day's UTC bounds, a `from|to` label becomes the range. Exported
 * so other list APIs (e.g. proposals) encode dates identically instead of
 * re-deriving the convention.
 */
export const handleDateRangeFilter: CustomFilterHandler = (filter, filterObj) => {
  const key = filter.filterBy;
  const raw = filter.filterValue.value;

  if (raw.includes("|")) {
    const range = parseDateRangeToGteLte(raw);
    if (range) filterObj[key] = range;
    return;
  }

  const singleDate = toValidDate(raw);
  if (!singleDate) return;
  const startOfDay = new Date(
    Date.UTC(
      singleDate.getUTCFullYear(),
      singleDate.getUTCMonth(),
      singleDate.getUTCDate(),
      0,
      0,
      0,
      0,
    ),
  );
  const endOfDay = new Date(
    Date.UTC(
      singleDate.getUTCFullYear(),
      singleDate.getUTCMonth(),
      singleDate.getUTCDate(),
      23,
      59,
      59,
      999,
    ),
  );
  filterObj[key] = { gte: startOfDay.toISOString(), lte: endOfDay.toISOString() };
};

const WO_DATE_KEYS = [
  "createdAt",
  "expectedOnsiteDate",
  "expectedCompletionDate",
  "completionDate",
  "scheduledTime",
  "finalVisit",
  "nextVisitDate",
] as const;

/**
 * SERV-2969: for a @db.Date column (`completionDate`), stored at 00:00Z of its day. The chip sends the
 * picked days as local-midnight instants, which `handleDateRangeFilter` compares as instants: a range
 * start of 05:00Z (Chicago) drops its own day, and a Beirut 21:00Z reads as the day before. Send the
 * local calendar day of each bound as UTC midnight instead; the backend compares inclusively.
 */
const handleCalendarDayFilter: CustomFilterHandler = (filter, filterObj) => {
  const [first, second] = filter.filterValue.value.split("|");
  const start = toValidDate(first);
  const end = second === undefined ? start : toValidDate(second);
  if (!start || !end) return;
  const [from, to] = end < start ? [end, start] : [start, end];
  filterObj[filter.filterBy] = { gte: toStoredCalendarDay(from), lte: toStoredCalendarDay(to) };
};

const woDateRangeHandlers: Record<string, CustomFilterHandler> = {
  ...Object.fromEntries(WO_DATE_KEYS.map((key) => [key, handleDateRangeFilter])),
  completionDate: handleCalendarDayFilter,
};

const handleNteRangeFilter: CustomFilterHandler = (filter, filterObj) => {
  try {
    const parsed = JSON.parse(filter.filterValue.id) as {
      min: number | null;
      max: number | null;
    };
    const range: Record<string, string> = {};
    if (parsed.min != null) range.gte = String(parsed.min);
    if (parsed.max != null) range.lte = String(parsed.max);
    if (Object.keys(range).length > 0) {
      filterObj[filter.filterBy] = range;
    }
  } catch {
    // ignore malformed numerical range
  }
};

const handleScheduledTimeFilter: CustomFilterHandler = (filter, filterObj) => {
  const key = filter.filterBy;
  const raw = filter.filterValue.value;

  if (raw.includes("|")) {
    const range = parseDateRangeToGteLte(raw);
    if (range) filterObj[key] = range;
    return;
  }

  const date = toValidDate(raw);
  if (!date) return;
  const ts = date.toISOString();
  const existing: string[] = (filterObj[key] as string[] | undefined) ?? [];
  if (!existing.includes(ts)) {
    filterObj[key] = [...existing, ts];
  }
};

export const WorkOrdersQueryKeys = {
  canCreateForClient: "workOrderCanCreateForClient",
  list: "workOrdersList",
  upcoming: "upcomingWorkOrders",
  dropdown: "workOrdersDropdown",
  woDetailById: "workOrderDetailById",
  geographyStates: "geographyStates",
  geographyCities: "geographyCities",
} as const;

export const getFilterWOListParams = (options: RequestQueryOptions) => {
  const params = new URLSearchParams();

  params.append("page", options.page.toString());
  params.append("limit", options.pageSize.toString());

  if (options.searchTerm || options.search) {
    params.append("search", options.search!);
  }

  if (options.sortBy) {
    params.append("sortBy", options.sortBy);
  }

  if (options.sortOrder) {
    params.append("sortOrder", options.sortOrder);
  }

  const filterBy = buildFilterByParam(options.filters, {
    getValue: (f) => {
      const useValue =
        f.filterBy === "client.businessName" ||
        f.filterBy === "internalPriority.value" ||
        f.filterBy === "location.siteId" ||
        f.filterBy === "location.address.address1" ||
        f.filterBy === "workOrderNumber";
      return useValue ? f.filterValue.value : f.filterValue.id;
    },
    customHandlers: {
      ...woDateRangeHandlers,
      nte: handleNteRangeFilter,
    },
  });
  if (filterBy) {
    params.append("filterBy", filterBy);
  }
  return params;
};

export const getWOData = async ({
  options,
  listType,
  signal,
  actingEntityId,
}: TWODataRequest): Promise<TCompletedWorkOrder> => {
  const params = getFilterWOListParams(options);

  const response = await axiosInstance.get(`/work-order/list?${params}&listType=${listType}`, {
    signal,
    actingEntityId,
  } as CustomAxiosRequestConfig);

  return response.data.data;
};

export const getUpcomingWOData = async ({
  options,
  signal,
  actingEntityId,
}: {
  options: {
    page: number;
    pageSize: number;
    search?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    filters?: Array<{ filterBy: string; filterValue: { id: string; value: string } }>;
  };
  signal?: AbortSignal;
  actingEntityId?: string;
}): Promise<TUpcomingWOResponse> => {
  const params = new URLSearchParams();

  params.append("page", options.page.toString());
  params.append("limit", options.pageSize.toString());

  if (options.search) {
    params.append("search", options.search);
  }

  if (options.sortBy) {
    params.append("sortBy", options.sortBy);
  }

  if (options.sortOrder) {
    params.append("sortOrder", options.sortOrder);
  }

  const filterBy = buildFilterByParam(options.filters, {
    getValue: (f) => f.filterValue.id,
    customHandlers: {
      scheduledTime: handleScheduledTimeFilter,
    },
  });
  if (filterBy) {
    params.append("filterBy", filterBy);
  }

  const queryString = getQueryParamsWithCustomQueryObj(params);
  const response = await axiosInstance.get(`/visit/upcoming?${queryString}`, {
    signal,
    actingEntityId,
  } as CustomAxiosRequestConfig);

  return response?.data?.data;
};

export const getWODropdown = async (
  _type: string,
  search?: string,
  signal?: AbortSignal,
  key?: string,
  searchParams?: TSearchParams,
): Promise<any> => {
  const params = new URLSearchParams();

  if (search) {
    params.append("search", search);
  }

  if (key === "workOrderNumber") {
    params.append("field", "workOrderNumber");
    params.append("alias", `{"source":"workOrderNumber","target":"label"}`);
    params.append("distinct", "true");
    params.append("modify", `{"source":["workOrderNumber"],"target":"id"}`);
  }

  if (key === "woStatus") {
    params.append("type", "LookupTypes");
    params.append("field", "id");
    params.append("field", "label");
    params.append("filterBy", "type");
    params.append("filterValue", "work_order_status");
  }

  if (key === "clientName") {
    if (searchParams?.type === "upcoming") {
      params.append("type", "upcoming");
      params.append("field", "client");
    } else if (searchParams?.type === "Clients") {
      params.append("type", "Clients");
      params.append("field", "id");
      params.append("field", "businessName");
    } else {
      params.append("type", "WorkOrders");
      params.append("field", "clientId");
      params.append("distinct", "true");
      params.append("includes", "client");
      params.append("filterBy", "woStatus.value");
      params.append("filterValue", "completed");
      params.append("filterValue", "closed");
      params.append("filterValue", "cancelled");
      params.append("filterValue", "ready_for_invoice");
      if (searchParams?.filterOperation === "not") {
        params.append("filterOperation", "not");
      }
      params.append("alias", `{"source":"client.businessName","target":"label"}`);
    }
  }

  if (key === "internalPriority") {
    params.append("type", "LookupTypes");
    params.append("field", "id");
    params.append("field", "label");
    params.append("filterBy", "type");
    params.append("filterValue", "internal_priority");
  }

  if (key === "visitsCount") {
    params.append("type", "WorkOrders");
    params.append("field", "visitsCount");
    params.append("distinct", "true");
    params.append("filterBy", "woStatus.value");
    params.append("filterValue", "completed");
    params.append("filterValue", "closed");
    params.append("filterValue", "cancelled");
    params.append("filterValue", "ready_for_invoice");
    params.append("filterOperation", "not");
  }

  if (key === "completedVisitsCount") {
    params.append("type", "WorkOrders");
    params.append("field", "visitsCount");
    params.append("alias", `{"source":"visitsCount","target":"label"}`);
    params.append("distinct", "true");
    params.append("modify", `{"source":["visitsCount"],"target":"id"}`);
    params.append("includes", "woStatus");
    params.append("filterBy", "woStatus.value");
    params.append("filterValue", "completed");
  }

  if (key === "nextVisit") {
    params.append("type", "workOrders");
    params.append("field", "nextVisit");
  }

  if (key === "nextVisitType") {
    params.append("type", "workOrders");
    params.append("field", "nextVisitType");
  }

  if (key === "trade") {
    params.append("type", "workOrders");
    params.append("field", "trade");
  }

  if (key === "createdAt") {
    params.append("type", "workOrders");
    params.append("field", "createdAt");
  }

  if (key === "expectedOnsiteDate") {
    params.append("type", "workOrders");
    params.append("field", "expectedOnsiteDate");
  }

  if (key === "expectedCompletionDate") {
    params.append("type", "workOrders");
    params.append("field", "expectedCompletionDate");
  }

  if (key === "workDescription") {
    params.append("type", "workOrders");
    params.append("field", "workDescription");
  }

  if (key === "siteId") {
    params.append("type", "workOrders");
    params.append("field", "siteId");
    params.append("filterBy", "woStatus.value");
    params.append("filterValue", "ready_for_invoice");
    params.append("filterValue", "completed");
    params.append("filterValue", "closed");
    params.append("filterValue", "cancelled");
    params.append("filterOperation", "not");
    params.append("modify", `{"source":["siteId"],"target":"label"}`);
    params.append("distinct", "true");
  }

  if (key === "nte") {
    params.append("type", "workOrders");
    params.append("field", "nte");
  }

  if (key === "visitNumber") {
    if (searchParams?.type === "upcoming") {
      params.append("type", "upcoming");
      params.append("field", "visitNumber");
    } else {
      params.append("type", "LookupTypes");
      params.append("field", "label");
      params.append("field", "value");
      params.append("filterBy", "type");
      params.append("filterValue", "visitNumber");
    }
  }

  if (key === "visitType") {
    params.append("type", "LookupTypes");
    params.append("field", "id");
    params.append("field", "label");
    params.append("filterBy", "type");
    params.append("filterValue", "visit_type");
  }

  if (key === "workOrderNumber") {
    params.append("type", "workOrders");
    params.append("field", "clientWorkOrderNumber");
  }

  if (key === "completedWorkOrderNumber") {
    params.append("type", "completed");
    params.append("field", "workOrderNumber");
  }

  if (key === "upcomingWorkOrderNumber") {
    params.append("type", "upcoming");
    params.append("field", "workOrderNumber");
  }

  if (key === "scheduledTime") {
    params.append("type", "workOrders");
    params.append("field", "scheduledTime");
  }

  if (key === "assignedServiceProvider") {
    if (searchParams?.type === "upcoming") {
      params.append("type", "upcoming");
      params.append("field", "serviceProvider");
    } else {
      params.append("type", "ServiceProviderInformation");
      params.append("field", "lastName");
      params.append("field", "firstName");
      params.append("modify", `{"source":["firstName","lastName"],"target":"label"}`);
    }
  }

  if (key === "visitStatus") {
    params.append("type", "LookupTypes");
    params.append("field", "id");
    params.append("field", "label");
    params.append("filterBy", "type");
    params.append("filterValue", "visit_status");
  }

  if (key === "locationSiteId") {
    if (searchParams?.type === "upcoming") {
      params.append("type", "upcoming");
      params.append("field", "siteId");
    } else {
      params.append("type", "workOrders");
      params.append("field", "siteId");
    }
  }

  if (key === "locationAddress") {
    params.append("type", "workOrders");
    params.append("field", "address");
  }

  if (key === "upcomingLocationAddress") {
    params.append("type", "upcoming");
    params.append("field", "locationAddress");
  }

  if (key === "clientWorkOrderNumber") {
    params.append("type", "workOrders");
    params.append("field", "workOrderNumber");
    params.append("distinct", "true");
    params.append("alias", `{"source":"workOrderNumber","target":"label"}`);
    params.append("modify", `{"source":["workOrderNumber"],"target":"id"}`);
    if (searchParams?.clientId) {
      params.append("filterBy", "clientId");
      params.append("filterValue", String(searchParams.clientId));
    }
  }

  if (key === "clientPriority") {
    params.append("type", "LookupTypes");
    params.append("field", "id");
    params.append("field", "label");
    params.append("filterBy", "type");
    params.append("filterValue", "client_priority");
  }

  if (key === "workOrderType") {
    params.append("type", "LookupTypes");
    params.append("field", "id");
    params.append("field", "label");
    params.append("filterBy", "type");
    params.append("filterValue", "work_order_type");
  }

  if (key === "clientSiteId") {
    params.append("type", "clientLocations");
    params.append("field", "siteId");
    params.append("distinct", "true");
    params.append("modify", `{"source":["siteId"],"target":"label"}`);
    if (searchParams?.clientId) {
      params.append("clientId", String(searchParams.clientId));
    }
  }

  if (key === "workDescription") {
    if (searchParams?.clientId) {
      params.append("filterBy", "clientId");
      params.append("filterValue", String(searchParams.clientId));
    }
  }

  if (key === "checkInStatus") {
    params.append("type", "LookupTypes");
    params.append("field", "id");
    params.append("field", "label");
    params.append("filterBy", "type");
    params.append("filterValue", "check_in_status");
  }

  if (key === "checkOutStatus") {
    params.append("type", "LookupTypes");
    params.append("field", "id");
    params.append("field", "label");
    params.append("filterBy", "type");
    params.append("filterValue", "check_out_status");
  }

  if (key === "businessName") {
    params.append("type", "WorkOrder");
    params.append("field", "id");
    params.append("field", key);
  }

  if (key === "status") {
    params.append("type", "LookupTypes");
    params.append("field", "value");
    params.append("filterBy", "type");
    params.append("filterValue", "wo_status");
  }

  if (key === "woSuccessManager") {
    params.append("type", "WorkOrder");
    params.append("field", "id");
    params.append("field", key);
    params.append("includes", "woSuccessManager");
  }

  if (key === "woType") {
    params.append("type", "LookupTypes");
    params.append("field", "value");
    params.append("filterBy", "type");
    params.append("filterValue", "workorder_type");
  }

  if (key === "activeNextVisitDate") {
    params.append("type", "active");
    params.append("field", "nextVisitDate");
  }

  if (key === "activeTrade") {
    params.append("type", "active");
    params.append("field", "trade");
  }

  if (key === "activeNextVisitType") {
    params.append("type", "active");
    params.append("field", "nextVisitType");
  }

  if (key == "address") {
    params.append("limit", "100");
  }

  const isAddressKey = key === "address";
  const isUpcomingClientNameKey = key === "clientName" && searchParams?.type === "upcoming";
  const isUpcomingVisitNumberKey = key === "visitNumber" && searchParams?.type === "upcoming";
  const isUpcomingAssignedSPKey =
    key === "assignedServiceProvider" && searchParams?.type === "upcoming";
  const isUpcomingLocationSiteIdKey = key === "locationSiteId" && searchParams?.type === "upcoming";
  const isUpcomingWorkOrderFilterNumberKey =
    key === "upcomingWorkOrderNumber" && searchParams?.type === "upcoming";
  const isUpcomingLocationAddressKey = key === "upcomingLocationAddress";
  const isCompletedWorkOrderFilterNumberKey =
    key === "completedWorkOrderNumber" && searchParams?.type === "completed";
  const isActiveNextVisitDateKey = key === "activeNextVisitDate";
  const isActiveTradeKey = key === "activeTrade";
  const isActiveNextVisitTypeKey = key === "activeNextVisitType";
  const isUpcomingWorkOrderNumberKey = key === "upcomingWorkOrderNumber";
  const isCompletedWorkOrderNumberKey = key === "completedWorkOrderNumber";
  const clientDropdownId = searchParams?.clientId;
  const isAssignedSPKey = key === "assignedSP" && !!clientDropdownId;
  const isClientTradeKey = key === "clientTrade" && !!clientDropdownId;
  const isClientZipcodeKey = key === "clientZipcode" && !!clientDropdownId;
  const isClientLocationAddressKey = key === "clientLocationAddress";

  let endpoint: string;
  switch (true) {
    case isAssignedSPKey:
      endpoint = `${workOrderEndpoints.getDropdown}/client/${clientDropdownId}/work-order-assigned-sps`;
      break;
    case isClientTradeKey:
      endpoint = `${workOrderEndpoints.getDropdown}/client/${clientDropdownId}/work-order-trades`;
      break;
    case isClientZipcodeKey:
      endpoint = `${workOrderEndpoints.getDropdown}/client/${clientDropdownId}/location-zipcodes`;
      break;
    case isClientLocationAddressKey:
      endpoint = `${workOrderEndpoints.getDropdown}/client-location-addresses`;
      break;
    case isAddressKey:
      endpoint = workOrderEndpoints.workOrderAddresses;
      break;
    case isUpcomingClientNameKey:
    case isUpcomingVisitNumberKey:
    case isUpcomingAssignedSPKey:
    case isUpcomingLocationSiteIdKey:
    case isUpcomingWorkOrderFilterNumberKey:
    case isUpcomingLocationAddressKey:
    case isCompletedWorkOrderFilterNumberKey:
    case isActiveNextVisitDateKey:
    case isActiveTradeKey:
    case isActiveNextVisitTypeKey:
      endpoint = workOrderEndpoints.workOrderFilter;
      break;
    case isUpcomingWorkOrderNumberKey:
    case isCompletedWorkOrderNumberKey:
      endpoint = workOrderEndpoints.workOrderNumbers;
      break;
    default:
      endpoint = workOrderEndpoints.dropdown;
  }

  const isSimpleSearchEndpoint =
    isAssignedSPKey || isClientTradeKey || isClientZipcodeKey || isClientLocationAddressKey;

  const queryString =
    isAddressKey || isSimpleSearchEndpoint
      ? search
        ? `search=${encodeURIComponent(search)}`
        : getQueryParamsWithCustomQueryObj(params)
      : isUpcomingWorkOrderNumberKey || isCompletedWorkOrderNumberKey
        ? getQueryParamsWithCustomQueryObj(params)
        : getQueryParamsWithCustomQueryObj(params);

  const response = await axiosInstance.get(`${endpoint}${queryString ? `?${queryString}` : ""}`, {
    signal,
  });

  if (key === "visitsCount" && response.data?.data) {
    const uniqueCounts = [
      ...new Set(response.data.data.map((item: any) => item.visitsCount ?? 0)),
    ]?.sort((a, b) => (a as number) - (b as number));

    response.data.data = uniqueCounts.map((count) => ({
      id: String(count),
      visitsCount: String(count),
    }));
  }

  if (key === "completedVisitsCount") {
    const dataArray = Array.isArray(response.data?.data) ? response.data.data : [];

    const transformedData = dataArray.map((item: any) => ({
      ...item,
      visitsCount: item.visitsCount != null ? String(item.visitsCount) : item.visitsCount,
      label: item.label != null ? String(item.label) : item.label,
      id: item.id != null ? String(item.id) : item.id,
    }));

    return {
      data: transformedData,
      pagination: response.data?.pagination || {},
    };
  }

  if (
    isAddressKey ||
    isUpcomingWorkOrderNumberKey ||
    isCompletedWorkOrderNumberKey ||
    isSimpleSearchEndpoint
  ) {
    const nestedData = response.data?.data;
    return {
      data: Array?.isArray(nestedData?.data)
        ? nestedData?.data
        : Array?.isArray(nestedData)
          ? nestedData
          : [],
      pagination: nestedData?.pagination || {},
    };
  }
  return response.data;
};

export interface WorkOrderAddressOption {
  id: string;
  value: string;
  label: string;
}

export interface WorkOrderAddressDropdownResponse {
  data: WorkOrderAddressOption[];
  pagination: { limit: number; nextCursor: string | null; hasMore: boolean };
}

export const getWorkOrderAddressDropdown = async ({
  search,
  cursor,
  limit = 20,
  listType,
  signal,
}: {
  search?: string;
  cursor?: string | null;
  limit?: number;
  listType?: "new" | "active" | "completed";
  signal?: AbortSignal;
}): Promise<WorkOrderAddressDropdownResponse> => {
  const params = new URLSearchParams();
  params.append("limit", String(limit));
  if (search) params.append("search", search);
  if (cursor) params.append("cursor", cursor);
  if (listType) params.append("listType", listType);

  const response = await axiosInstance.get(
    `${workOrderEndpoints.workOrderAddresses}?${params.toString()}`,
    { signal },
  );

  const nested = response.data?.data;
  return {
    data: Array.isArray(nested?.data) ? nested.data : Array.isArray(nested) ? nested : [],
    pagination: nested?.pagination ?? { limit, nextCursor: null, hasMore: false },
  };
};

export const getWorkOrderById = async (workOrderId: string): Promise<TWorkOrderByIdResponse> => {
  const response = await axiosInstance.get(`${workOrderEndpoints.getWorkOrderById}/${workOrderId}`);
  return response.data;
};

// ── Client/location work orders (workOrders.queries) ──────────────────────

const transformApiResponseToTableData = (
  apiData: UpcomingVisitApiResponse[],
): UpcomingTabType[] => {
  return apiData.map((item) => ({
    id: item.visitId,
    visitNumber: `Visit ${item.visitNumber}`,
    visitType: item.visitType,
    workOrderNumber: {
      id: item.workOrderId,
      value: item.workOrderNumber,
      label: item.workOrderLabel,
    },
    clientName: item.clientName,
    assignedSP:
      item.providers && item.providers.length > 0
        ? {
            id: item.providers[0].providerId,
            name: item.providers[0].providerName,
          }
        : null,
    visitTime: item.scheduledTime,
    visitStatus: item.status,
    checkIn: item.checkInStatus ?? null,
    checkOut: item.checkOutStatus ?? null,
    locationSiteId: item.locationSiteId,
  }));
};

export const transformWorkOrderToActiveTab = (workOrder: WorkOrder): ActiveTabType => {
  const trade = workOrder.WorkOrderTrades?.[0]?.clientTrade?.trade?.name || "-";

  return {
    id: workOrder.id,
    workOrderNumber: {
      id: workOrder.id,
      value: workOrder.workOrderNumber,
      label: workOrder.workOrderLabel,
    },
    woStatus: {
      id: workOrder.woStatus.id,
      value: workOrder.woStatus.value,
      label: workOrder.woStatus.label,
    },
    priority: {
      id: workOrder.internalPriority.id,
      value: workOrder.internalPriority.value,
      label: workOrder.internalPriority.label,
    },
    workOrderType: workOrder.workOrderType.label,
    visitsCount: workOrder.visitsCount,
    nextVisit: workOrder.nextVisitTime,
    nextVisitType: workOrder.nextVisitType,
    assignedSP: null,
    trade,
    createdDate: workOrder.createdAt,
    expectedOnsiteDate: workOrder.expectedOnsiteDate,
    expectedCompletionDate: workOrder.expectedCompletionDate,
    description: workOrder.workDescription,
  };
};

export const transformWorkOrderToCompletedTab = (
  workOrder: CompletedWorkOrder,
): CompletedTabType => {
  const lastVisit = workOrder.Visits?.[workOrder.Visits.length - 1];
  const finalVisit = lastVisit?.scheduledTime || "-";

  const totalAmount = workOrder.totalAmount
    ? `$${parseFloat(workOrder.totalAmount).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : "-";

  const paymentStatus = {
    id: workOrder.paymentStatus || "",
    value: workOrder.paymentStatus || "",
    label: workOrder.paymentStatus || "-",
  };

  return {
    id: workOrder.id,
    workOrderNumber: {
      id: workOrder.id,
      value: workOrder.workOrderNumber,
      label: workOrder.workOrderLabel,
    },
    paymentStatus,
    totalVisits: workOrder.visitsCount,
    finalVisit,
    totalAmount,
    completionDate: workOrder.completionDate,
  };
};

type LocationWorkOrderListOptions = WorkOrderListQueryOptions & { locationId: string };

function appendLocationListParams(
  params: URLSearchParams,
  options: LocationWorkOrderListOptions,
  listType: "active" | "completed",
): void {
  const { page = 1, limit = 10, locationId, search, sortBy, sortOrder, filters } = options;

  const filterBy: Record<string, unknown> = {};
  if (locationId) {
    filterBy.locationId = [locationId];
  }

  if (filters?.length) {
    const userFilterObj = buildFilterByObject(filters, {
      getValue: (f) => {
        const useValue =
          f.filterBy === "workOrderNumber" ||
          f.filterBy === "client.businessName" ||
          f.filterBy === "nextVisitType";
        return useValue ? f.filterValue.value : f.filterValue.id;
      },
      customHandlers: {
        ...woDateRangeHandlers,
        nextVisitDate: handleDateRangeFilter,
        nte: handleNteRangeFilter,
      },
    });
    if (userFilterObj) {
      Object.assign(filterBy, userFilterObj);
    }
  }

  params.append("page", page.toString());
  params.append("limit", limit.toString());
  params.append("listType", listType);

  if (Object.keys(filterBy).length > 0) {
    params.append("filterBy", JSON.stringify(filterBy));
  }

  if (search) params.append("search", search);
  if (sortBy) params.append("sortBy", sortBy);
  if (sortOrder) params.append("sortOrder", sortOrder);
}

export const getWorkOrdersList = async (
  options: LocationWorkOrderListOptions,
  signal?: AbortSignal,
): Promise<{ data: ActiveTabType[]; pagination: any }> => {
  const queryParams = new URLSearchParams();
  appendLocationListParams(queryParams, options, "active");

  const response = await axiosInstance.get<WorkOrderListResponse>(
    `${workOrdersEndpoints.list}?${queryParams.toString()}`,
    { signal },
  );

  const { data, pagination } = response.data.data;
  return {
    data: data.map(transformWorkOrderToActiveTab),
    pagination,
  };
};

export const getCompletedWorkOrdersList = async (
  options: LocationWorkOrderListOptions,
  signal?: AbortSignal,
): Promise<{ data: CompletedTabType[]; pagination: any }> => {
  const queryParams = new URLSearchParams();
  appendLocationListParams(queryParams, options, "completed");

  const response = await axiosInstance.get<WorkOrderListResponse>(
    `${workOrdersEndpoints.list}?${queryParams.toString()}`,
    { signal },
  );

  const { data, pagination } = response.data.data;
  return {
    data: data.map((wo: unknown) => transformWorkOrderToCompletedTab(wo as CompletedWorkOrder)),
    pagination,
  };
};

export type LocationUpcomingVisitsOptions = {
  locationId: string;
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  filters?: Array<{ filterBy: string; filterValue: { id: string; value: string } }>;
};

export const getUpcomingVisits = async (
  options: LocationUpcomingVisitsOptions,
  signal?: AbortSignal,
): Promise<{ data: UpcomingTabType[]; pagination: any }> => {
  const { locationId, page = 1, limit = 10, search, sortBy, sortOrder, filters } = options;

  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("limit", limit.toString());

  if (search) params.append("search", search);
  if (sortBy) params.append("sortBy", sortBy);
  if (sortOrder) params.append("sortOrder", sortOrder);

  const filterByObj: Record<string, unknown> = { locationId: [locationId] };

  if (filters?.length) {
    const userFilterObj = buildFilterByObject(filters, {
      getValue: (f) => {
        const useValue = f.filterBy === "visitNumber" || f.filterBy === "clientName";
        return useValue ? f.filterValue.value : f.filterValue.id;
      },
      customHandlers: {
        scheduledTime: handleScheduledTimeFilter,
      },
    });
    if (userFilterObj) Object.assign(filterByObj, userFilterObj);
  }

  params.append("filterBy", JSON.stringify(filterByObj));

  const queryString = getQueryParamsWithCustomQueryObj(params);
  const response = await axiosInstance.get<{
    success: boolean;
    data: { data: UpcomingVisitApiResponse[]; pagination: any };
  }>(`${workOrdersEndpoints.upcomingVisits}?${queryString}`, { signal });

  return {
    data: transformApiResponseToTableData(response.data.data.data),
    pagination: response.data.data.pagination,
  };
};

export interface ValidateAddressResponse {
  valid: boolean;
  reason?: string;
  message?: string;
}

export const validateAddress = async (
  zipcode: string,
  state: string,
): Promise<ValidateAddressResponse> => {
  const response = await axiosInstance.post(workOrderEndpoints.validateAddress, {
    zipcode,
    state,
  });
  return response.data.data;
};

export const getGeographyStates = async (): Promise<GeographyRegion[]> => {
  const response = await axiosInstance.get(workOrderEndpoints.geographyStates);
  return response.data.data;
};

export const getGeographyCities = async (
  stateCode: string,
  signal?: AbortSignal,
): Promise<GeographyCitiesResponse> => {
  const limit = 500;
  const firstRes = await axiosInstance.get(
    `${workOrderEndpoints.geographyCities}/${stateCode}?limit=${limit}&page=1`,
    { signal },
  );
  const firstPage: GeographyCitiesResponse = firstRes.data.data;
  const { total } = firstPage.pagination;

  if (total <= limit) {
    return firstPage;
  }

  const totalPages = Math.ceil(total / limit);
  const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
  const remainingResponses = await Promise.all(
    remainingPages.map((page) =>
      axiosInstance.get(
        `${workOrderEndpoints.geographyCities}/${stateCode}?limit=${limit}&page=${page}`,
        { signal },
      ),
    ),
  );

  return {
    ...firstPage,
    data: [
      ...firstPage.data,
      ...remainingResponses.flatMap((r) => (r.data.data as GeographyCitiesResponse).data),
    ],
    pagination: { ...firstPage.pagination, hasNext: false, hasPrev: false },
  };
};

export const getUpcomingVisitsBySiteId = async (
  siteId: string,
  signal?: AbortSignal,
): Promise<UpcomingTabType[]> => {
  const filterBy = JSON.stringify({ "workOrder.location.siteId": [siteId] });
  const url = `${workOrdersEndpoints.upcomingVisits}?filterBy=${encodeURIComponent(filterBy)}`;

  const response = await axiosInstance.get<{
    success: boolean;
    data: { data: UpcomingVisitApiResponse[] };
  }>(url, { signal });

  return transformApiResponseToTableData(response?.data?.data?.data);
};

export const getWorkOrderDetails = async (workOrderId: string) => {
  const response = await axiosInstance.get<WorkOrderDetailsResponse>(
    `${workOrderEndpoints.workOrderDetails}/${workOrderId}`,
  );
  return response?.data?.data;
};

// ── SERV-2682: bot intake drafts ──────────────────────────────────────────

export const getWorkOrderIntakeDrafts = async ({
  options,
  signal,
}: {
  options: {
    page: number;
    pageSize: number;
    search?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  };
  signal?: AbortSignal;
}): Promise<TWorkOrderIntakeDraftsResponse> => {
  const params = new URLSearchParams();
  params.append("page", String(options.page));
  params.append("limit", String(options.pageSize));
  if (options.search) params.append("search", options.search);
  if (options.sortBy) params.append("sortBy", options.sortBy);
  if (options.sortOrder) params.append("sortOrder", options.sortOrder);

  const response = await axiosInstance.get(`/work-order/intake-drafts?${params}`, { signal });

  return response.data.data;
};

export const getWorkOrderIntakeDraft = async (
  draftId: string,
  signal?: AbortSignal,
): Promise<TWorkOrderIntakeDraft> => {
  const response = await axiosInstance.get(`/work-order/intake-drafts/${draftId}`, { signal });

  return response.data.data;
};

export const dismissWorkOrderIntakeDraft = async (
  draftId: string,
  dismissReason: string | null,
): Promise<{ id: string; reviewStatus: string }> => {
  // The axios interceptor toasts any rejected response carrying success:false,
  // so without this the caller's own onError adds a second notification for the
  // same rejection. Same reason as procurement.mutations.ts.
  const response = await axiosInstance.post(
    `/work-order/intake-drafts/${draftId}/dismiss`,
    { dismissReason },
    { suppressErrorAlert: true } as CustomAxiosRequestConfig,
  );

  return response.data.data;
};

export const getCanCreateWorkOrderForClient = async (
  clientId: string,
  signal?: AbortSignal,
): Promise<{ allowed: boolean }> => {
  const response = await axiosInstance.get(workOrderEndpoints.canCreateForClient(clientId), {
    signal,
  });
  return response.data.data;
};
